/**
 * Хранение ролевой раскладки админки: локальный кэш + облако.
 *
 * Облако — источник истины (`GET/PUT /sync/role-navigation`), локально
 * держим копию в `meta`, чтобы админка открывалась офлайн. Конфликт
 * решается по `updatedAt` (last-write-wins).
 */

import { db } from '@core/db/db'
import { META_ROLE_NAVIGATION } from '@core/db/schema'
import { syncService } from '@core/services/sync/SyncService'
import {
  DEFAULT_ROLE_NAVIGATION,
  normalizeRoleNavigation,
  pickLatestRoleNavigation,
  sanitizeSections,
  type RoleNavigationConfig,
  type RolePresetId
} from './rolePresets'
import type { AdminSectionId } from '@core/pages/admin/types'

/** Локальная копия раскладки. Мусор в meta превращается в дефолт. */
export async function loadLocalRoleNavigation(): Promise<RoleNavigationConfig> {
  try {
    const entry = await db.meta.get(META_ROLE_NAVIGATION)
    return normalizeRoleNavigation(entry?.value)
  } catch {
    return { ...DEFAULT_ROLE_NAVIGATION, byRole: {} }
  }
}

export async function saveLocalRoleNavigation(
  config: RoleNavigationConfig
): Promise<void> {
  await db.meta.put({ key: META_ROLE_NAVIGATION, value: config })
}

/**
 * Актуальная раскладка: облако (если онлайн) против локального кэша.
 * Более поздняя версия побеждает и сразу кэшируется локально.
 */
export async function loadRoleNavigation(): Promise<RoleNavigationConfig> {
  const local = await loadLocalRoleNavigation()
  let remote: unknown = null
  try {
    remote = await syncService.fetchRoleNavigation()
  } catch {
    return local
  }
  if (!remote) return local
  const merged = pickLatestRoleNavigation(remote, local)
  if (merged.updatedAt !== local.updatedAt || remote) {
    await saveLocalRoleNavigation(merged).catch(() => undefined)
  }
  return merged
}

/**
 * Изменить раскладку одной роли. `sections` пустой массив — роль видит
 * только пустую навигацию; чтобы вернуть пресет, передайте `undefined`.
 */
export async function updateRoleNavigation(
  role: RolePresetId,
  sections: AdminSectionId[] | undefined
): Promise<RoleNavigationConfig> {
  const current = await loadLocalRoleNavigation()
  const byRole = { ...current.byRole }
  if (sections === undefined) {
    delete byRole[role]
  } else {
    byRole[role] = sanitizeSections(sections)
  }
  const next: RoleNavigationConfig = {
    version: current.version,
    updatedAt: Date.now(),
    byRole
  }
  await saveLocalRoleNavigation(next)
  // Best-effort: офлайн-правка уйдёт при следующем открытии админки онлайн.
  void syncService.pushRoleNavigation(next).catch(() => undefined)
  return next
}

/** Отправить локальную раскладку в облако (вызывается при открытии админки). */
export async function pushLocalRoleNavigation(): Promise<void> {
  const local = await loadLocalRoleNavigation()
  if (local.updatedAt === 0) return
  await syncService.pushRoleNavigation(local)
}
